import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  COLOR_TRAFFIC_LIGHT_NEUTRAL,
  getCssVariableValue,
} from 'frontend-utils/utils/css-variable';
import HotelShortTermRentalShortTermRentalBreakdownBlock from 'lumen/components/hotel/short-term-rental/short-term-rental-breakdown-block';
import { BREAKDOWN_TOOLTIP_TRANSLATIONS } from 'lumen/utils/constants/short-term-rental-constants';

import styles from './short-term-rental-breakdowns.module.css';

interface HotelShortTermRentalShortTermRentalBreakdownsSignature {
  Args: {
    activeFilters: unknown;
    addFilter: unknown;
    breakdowns: unknown;
    hasInvalidSelection: unknown;
    isLoading: unknown;
  };
}

export default class HotelShortTermRentalShortTermRentalBreakdowns extends Component<HotelShortTermRentalShortTermRentalBreakdownsSignature> {
  @service declare intl: Services['intl'];

  AGGREGATE = 'aggregate';

  sortOrder = [
    'price_type',
    'type',
    'subtype',
    'amenity',
    'capacity',
    'additional_fees',
    'min_stay',
    'review_score',
  ];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get breakdownBlocks() {
    if (this.args.isLoading) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return new Array(this.sortOrder.length).fill({});
    }

    const { breakdowns } = this.args;

    // Group data by type
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const groupedData = breakdowns.reduce((result, item) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { type } = item;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      result[type] = result[type] || [];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      result[type].push(item);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return result;
    }, {});

    // Sort each group by value
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(groupedData).forEach((type) => {
      if (type === 'capacity' || type === 'min_stay') {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        groupedData[type].sort((a, b) => a.subType - b.subType);
        return;
      }
      if (type === 'review_score') {
        const typeOrder = {
          5: 0,
          '5-': 1,
          '4;5-': 2,
          '4-': 3,
          '3;5-': 4,
        };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        groupedData[type].sort(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (a, b) => typeOrder[a.subType] - typeOrder[b.subType],
        );
        return;
      }
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      groupedData[type].sort((a, b) => b.value - a.value);
    });

    // Aggregate data after the fourth value for each type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(groupedData).forEach((type) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (groupedData[type].length > 5) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const sumOfFirstFourValues = groupedData[type]
          .slice(0, 4)
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          .reduce((sum, item) => sum + item.value, 0);
        // Because of rounding errors, the sum of the values after the fourth might not be 100%,
        // so we take the difference between 100 % and the sum of the first four values
        const aggregatedValue =
          sumOfFirstFourValues === 0 ? 0 : 1 - sumOfFirstFourValues;
        const aggregatedItem = {
          name: this.AGGREGATE,
          value: this.intl.formatNumber(aggregatedValue, { style: 'percent' }),
          progressBarData: [
            {
              widthPercentage: Math.round(aggregatedValue * 100),
              backgroundColor: getCssVariableValue(COLOR_TRAFFIC_LIGHT_NEUTRAL),
            },
          ],
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          amount: groupedData[type].slice(4).length,
        };
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const remainingItems = groupedData[type].splice(4);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        groupedData[type] = groupedData[type].concat(
          aggregatedItem,
          remainingItems,
        );
      }
    });

    // Format needed data
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(groupedData).forEach((type) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      groupedData[type] = {
        type,
        title: this.intl.t(`short_term_rental.breakdowns.${type}`),
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        values: groupedData[type].map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (item.name === this.AGGREGATE) {
            return {
              name: this.intl.t('insight_pages.breakdown.more', {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                count: item.amount,
              }),
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              value: type === 'amenity' ? null : item.value,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              progressBarData: type === 'amenity' ? null : item.progressBarData,
              type,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              subType: item.subType,
            };
          }
          return {
            isDisabled:
              this.args.hasInvalidSelection &&
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              !this.args.activeFilters.includes(item.key),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            name: item.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            key: item.key,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
            value: this.intl.formatNumber(item.value, { style: 'percent' }),
            progressBarData: [
              {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                widthPercentage: Math.round(item.value * 100),
                backgroundColor: getCssVariableValue(
                  COLOR_TRAFFIC_LIGHT_NEUTRAL,
                ),
              },
            ],
            type,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            subType: item.subType,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            tooltipMessage: BREAKDOWN_TOOLTIP_TRANSLATIONS[item.key]
              ? // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                this.intl.t(BREAKDOWN_TOOLTIP_TRANSLATIONS[item.key])
              : null,
          };
        }),
      };
    });

    /**
     * Filter out the "Extra fees per stay" tile.
     * This will later be removed by backend as well, when that is done, this filter can be removed.
     * See https://lighthouseintelligence.atlassian.net/browse/SH-40334
     */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const breakdownsArray = Object.values(groupedData)
      .flat()
      // @ts-expect-error: Incorrect type
      .filter((breakdown) => breakdown.type !== 'additional_fees');

    // Sort each block based on the sortOrder
    breakdownsArray.sort(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      (a, b) => this.sortOrder.indexOf(a.type) - this.sortOrder.indexOf(b.type),
    );

    return breakdownsArray;
  }

  <template>
    <div class={{styles.section}}>
      {{#each this.breakdownBlocks as |breakdown|}}
        <HotelShortTermRentalShortTermRentalBreakdownBlock
          @activeFilters={{@activeFilters}}
          @addFilter={{@addFilter}}
          @blockTitle={{breakdown.title}}
          @isLoading={{@isLoading}}
          @items={{breakdown.values}}
        />
      {{/each}}
    </div>
  </template>
}
